import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import './css/MachineUsage.css';
import './css/MachineStatusOverview.css';
import './css/MostIssuedItems.css';
import './css/CostVsUsage.css';
import './css/MachinesOutOfOrder.css';
import { MaximizeIcon } from '../../Icons/DashboardIcons';

// Import refactored chart components
import MostUsedMachinesChart from './charts/MostUsedMachinesChart';
import MachineStatusOverviewChart from './charts/MachineStatusOverviewChart';
import MostIssuedItemsChart from './charts/MostIssuedItemsChart';
import CostVsUsageChart from './charts/CostVsUsageChart';

// Import reusable components
import { FullscreenChartModal, ErrorDisplay, SampleDataBadge } from './common';

// Import API
import TLDashboardAPIs from '../../utilities/api/dashboard/TLDashboardAPIs';

// Import sample data utilities
import { isDataEmpty, getSampleDataForPeriod } from '../../utilities/sampleData/tlDashboardSampleData';

const MachineUsageTab = ({ selectedPeriod = 'weekly' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [maximizedChart, setMaximizedChart] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Fetch data when period or refreshTrigger changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await TLDashboardAPIs.getMachineUsage(selectedPeriod);
                setData(response);
            } catch (err) {
                console.error('Error fetching machine usage data:', err);
                setError('Failed to load machine usage data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedPeriod, refreshTrigger]);

    const handleRetry = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size={48} sx={{ color: '#007bff' }} />
                <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
                    Fetching machine usage data...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <ErrorDisplay message={error} onRetry={handleRetry} />;
    }

    // Extract data from API response with sample data fallback
    const rawMostUsed = data?.most_used_machines || [];
    const isMostUsedEmpty = isDataEmpty(rawMostUsed, 'array');
    const mostUsedMachines = isMostUsedEmpty ? getSampleDataForPeriod('mostUsedMachines') : rawMostUsed;

    const rawMachineStatus = data?.machine_status_overview || {
        operational: 0,
        under_maintenance: 0,
        out_of_order: 0,
        total_machines: 0
    };
    const isMachineStatusEmpty = rawMachineStatus.total_machines === 0;
    const machineStatusOverview = isMachineStatusEmpty ? getSampleDataForPeriod('machineStatusOverview') : rawMachineStatus;

    const rawMostIssued = data?.most_issued_items || [];
    const isMostIssuedEmpty = isDataEmpty(rawMostIssued, 'array');
    const mostIssuedItems = isMostIssuedEmpty ? getSampleDataForPeriod('mostIssuedItems') : rawMostIssued;

    const rawCostVsUsage = data?.cost_vs_usage || [];
    const isCostVsUsageEmpty = isDataEmpty(rawCostVsUsage, 'array');
    const costVsUsage = isCostVsUsageEmpty ? getSampleDataForPeriod('costVsUsage') : rawCostVsUsage;

    const rawMachinesOutOfOrder = data?.machines_out_of_order || [];
    const isMachinesOutOfOrderEmpty = rawMachinesOutOfOrder.length === 0;
    const machinesOutOfOrder = isMachinesOutOfOrderEmpty ? getSampleDataForPeriod('machinesOutOfOrder') : rawMachinesOutOfOrder;

    // Get period label for legend
    const periodLabel = selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1);

    // Helper function to get chart title
    const getChartTitle = (chartType) => {
        switch (chartType) {
            case 'mostUsedMachines': return 'Most Used Machines';
            case 'machineStatus': return 'Machine Status Overview';
            case 'mostIssuedItems': return 'Most Issued Items';
            case 'costVsUsage': return 'Cost of Machine vs Usage Hours';
            case 'machinesOutOfOrder': return 'Machines Out of Order';
            default: return '';
        }
    };

    // Helper function to render chart content in fullscreen
    const renderChartContent = (chartType) => {
        switch (chartType) {
            case 'mostUsedMachines':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        <Box sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            display: 'flex',
                            gap: '12px',
                            zIndex: 10,
                        }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box style={{
                                    border: selectedPeriod === 'weekly' ? '1px solid #0576d3' :
                                        selectedPeriod === 'monthly' ? '1px solid #c144ff' : '1px solid #d6a210',
                                    background: selectedPeriod === 'weekly' ? 'transparent' :
                                        selectedPeriod === 'monthly' ? 'rgba(215, 145, 255, 0.2)' : '#fff7d1',
                                    width: '26px', height: '13px', borderRadius: '2px'
                                }}></Box>
                                <Typography sx={{ fontSize: '14px', color: '#333' }}>{periodLabel}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MostUsedMachinesChart data={mostUsedMachines} selectedPeriod={selectedPeriod} />
                        </Box>
                    </Box>
                );
            case 'machineStatus':
                return (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MachineStatusOverviewChart data={machineStatusOverview} />
                    </Box>
                );
            case 'mostIssuedItems':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        <Box sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            display: 'flex',
                            gap: '12px',
                            zIndex: 10,
                        }}>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box style={{
                                    border: selectedPeriod === 'weekly' ? '1px solid #88c68a' :
                                        selectedPeriod === 'monthly' ? '1px solid #ffb366' : '1px solid #ff9999',
                                    background: selectedPeriod === 'weekly' ? 'rgba(136, 198, 138, 0.3)' :
                                        selectedPeriod === 'monthly' ? 'rgba(255, 179, 102, 0.3)' : 'rgba(255, 153, 153, 0.3)',
                                    width: '26px', height: '13px', borderRadius: '2px'
                                }}></Box>
                                <Typography sx={{ fontSize: '14px', color: '#333' }}>{periodLabel}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MostIssuedItemsChart data={mostIssuedItems} selectedPeriod={selectedPeriod} />
                        </Box>
                    </Box>
                );
            case 'costVsUsage':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <CostVsUsageChart data={costVsUsage} />
                        </Box>
                    </Box>
                );
            case 'machinesOutOfOrder':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                        <Box sx={{ overflowX: 'auto', width: '100%' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e0e0e0' }}>Machine Name</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e0e0e0' }}>Issue</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e0e0e0' }}>Downtime</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e0e0e0' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {machinesOutOfOrder.length > 0 ? (
                                        machinesOutOfOrder.map((machine, index) => (
                                            <tr key={index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                <td style={{ padding: '12px 16px', color: '#333' }}>{machine.machine_name || machine.name}</td>
                                                <td style={{ padding: '12px 16px', color: '#666' }}>{machine.issue || 'N/A'}</td>
                                                <td style={{ padding: '12px 16px', color: '#666' }}>{machine.downtime || 'N/A'}</td>
                                                <td style={{ padding: '12px 16px' }}>
                                                    <span style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        backgroundColor: '#ffebee',
                                                        color: '#c62828',
                                                        fontSize: '12px',
                                                        fontWeight: 500
                                                    }}>
                                                        {machine.status || 'Out of order'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
                                                No machines out of order
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '16px', sm: '20px', md: '24px' }, width: '100%' }}>
            {/* Fullscreen Chart Modal */}
            <FullscreenChartModal
                open={Boolean(maximizedChart)}
                onClose={() => setMaximizedChart(null)}
                title={getChartTitle(maximizedChart)}
            >
                {renderChartContent(maximizedChart)}
            </FullscreenChartModal>
            {/* First Row - Most Used Machines and Machine Status Overview */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: { xs: '16px', sm: '20px', md: '24px' },
                width: '100%'
            }}>
                {/* Most Used Machines Chart */}
                <Box className="most-used-machines" sx={{ flex: 1, maxWidth: { xs: '100%', lg: '50%' }, minWidth: 0, height: { xs: '280px', sm: '300px', md: '350px', lg: '450px' }, maxHeight: { xs: '280px', sm: '300px', md: '350px', lg: '450px' } }}>
                    <Box className="header">
                        <Box className="frame-1000001142" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography className="title">Most Used Machines</Typography>
                            <SampleDataBadge show={isMostUsedEmpty} absolute={false} />
                        </Box>
                        <Box className="controls">
                            <Box
                                className="drop-down"
                                onClick={() => setMaximizedChart('mostUsedMachines')}
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                            >
                                <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MaximizeIcon color="#333" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box className="content">
                        <Box className="content2" style={{ position: 'relative' }}>
                            {/* Custom Legend - shows current period */}
                            <Box sx={{
                                position: 'absolute',
                                top: { xs: '6px', sm: '8px', md: '12px' },
                                right: { xs: '6px', sm: '8px', md: '12px' },
                                display: 'flex',
                                gap: { xs: '6px', md: '12px' },
                                zIndex: 10,
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                                maxWidth: '100%'
                            }}>
                                <Box className="container" sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', md: '6px' } }}>
                                    <Box className="legend-color-box" sx={{
                                        border: selectedPeriod === 'weekly' ? '1px solid #0576d3' :
                                            selectedPeriod === 'monthly' ? '1px solid #c144ff' : '1px solid #d6a210',
                                        background: selectedPeriod === 'weekly' ? 'transparent' :
                                            selectedPeriod === 'monthly' ? 'rgba(215, 145, 255, 0.2)' : '#fff7d1',
                                        width: { xs: '18px', md: '26px' }, height: { xs: '10px', md: '13px' }, borderRadius: '2px'
                                    }}></Box>
                                    <Typography sx={{ fontSize: { xs: '10px', md: '12px' }, color: '#333' }}>{periodLabel}</Typography>
                                </Box>
                            </Box>
                            <MostUsedMachinesChart data={mostUsedMachines} selectedPeriod={selectedPeriod} />
                        </Box>
                    </Box>
                </Box>

                {/* Machine Status Overview Chart */}
                <Box className="machine-status-overview" sx={{ flex: 1, maxWidth: { xs: '100%', lg: '50%' }, minWidth: 0, height: { xs: '280px', sm: '300px', md: '350px', lg: '450px' }, maxHeight: { xs: '280px', sm: '300px', md: '350px', lg: '450px' } }}>
                    <Box className="header">
                        <Box className="frame-1000001142" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography className="title">Machine Status Overview</Typography>
                            <SampleDataBadge show={isMachineStatusEmpty} absolute={false} />
                        </Box>
                        <Box className="controls">
                            <Box
                                className="drop-down"
                                onClick={() => setMaximizedChart('machineStatus')}
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                            >
                                <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MaximizeIcon color="#333" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box className="content">
                        <Box className="chart">
                            <MachineStatusOverviewChart data={machineStatusOverview} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Second Row - Most Issued Items and Cost vs Usage Hours */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: { xs: '16px', sm: '20px', md: '24px' },
                width: '100%'
            }}>
                {/* Most Issued Items Chart */}
                <Box className="most-issued-items" sx={{ flex: 1, maxWidth: { xs: '100%', lg: '50%' }, minWidth: 0, height: { xs: '280px', sm: '300px', md: '350px', lg: '450px' }, maxHeight: { xs: '280px', sm: '300px', md: '350px', lg: '450px' } }}>
                    <Box className="header">
                        <Box className="frame-1000001142" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography className="title">Most Issued Items</Typography>
                            <SampleDataBadge show={isMostIssuedEmpty} absolute={false} />
                        </Box>
                        <Box className="controls">
                            <Box
                                className="drop-down"
                                onClick={() => setMaximizedChart('mostIssuedItems')}
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                            >
                                <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MaximizeIcon color="#333" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box className="content">
                        <Box className="content2" style={{ position: 'relative' }}>
                            {/* Custom Legend - shows current period */}
                            <Box sx={{
                                position: 'absolute',
                                top: { xs: '6px', sm: '8px', md: '12px' },
                                right: { xs: '6px', sm: '8px', md: '12px' },
                                display: 'flex',
                                gap: { xs: '6px', md: '12px' },
                                zIndex: 10,
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                                maxWidth: '100%'
                            }}>
                                <Box className="container" sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', md: '6px' } }}>
                                    <Box className="legend-color-box" sx={{
                                        border: selectedPeriod === 'weekly' ? '1px solid #88c68a' :
                                            selectedPeriod === 'monthly' ? '1px solid #ffb366' : '1px solid #ff9999',
                                        background: selectedPeriod === 'weekly' ? 'rgba(136, 198, 138, 0.3)' :
                                            selectedPeriod === 'monthly' ? 'rgba(255, 179, 102, 0.3)' : 'rgba(255, 153, 153, 0.3)',
                                        width: { xs: '18px', md: '26px' }, height: { xs: '10px', md: '13px' }, borderRadius: '2px'
                                    }}></Box>
                                    <Typography sx={{ fontSize: { xs: '10px', md: '12px' }, color: '#333' }}>{periodLabel}</Typography>
                                </Box>
                            </Box>
                            <MostIssuedItemsChart data={mostIssuedItems} selectedPeriod={selectedPeriod} />
                        </Box>
                    </Box>
                </Box>

                {/* Cost vs Usage Hours Chart */}
                <Box className="cost-of-machine-vs-usage-hours" sx={{ flex: 1, maxWidth: { xs: '100%', lg: '50%' }, minWidth: 0, height: { xs: '280px', sm: '300px', md: '350px', lg: '450px' }, maxHeight: { xs: '280px', sm: '300px', md: '350px', lg: '450px' } }}>
                    <Box className="header">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography className="title">Cost of Machine vs Usage Hours</Typography>
                            <SampleDataBadge show={isCostVsUsageEmpty} absolute={false} />
                        </Box>
                        <Box className="controls">
                            <Box
                                className="drop-down"
                                onClick={() => setMaximizedChart('costVsUsage')}
                                sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { opacity: 0.7 } }}
                            >
                                <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MaximizeIcon color="#333" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box className="content">
                        <Box className="content2">
                            <CostVsUsageChart data={costVsUsage} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Third Row - Machines Out of Order Table */}
            <Box className="machines-out-of-order">
                <Box className="header">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '6px', md: '8px' }, flexWrap: 'wrap' }}>
                        <Typography className="title">Machines Out of Order</Typography>
                        <SampleDataBadge show={isMachinesOutOfOrderEmpty} absolute={false} />
                    </Box>
                    <Box className="controls">
                        <Box sx={{
                            padding: { xs: '3px 8px', md: '4px 12px' },
                            bgcolor: '#f5f5f5',
                            borderRadius: '4px',
                            fontSize: { xs: '10px', md: '12px' },
                            color: '#666'
                        }}>
                            {periodLabel}
                        </Box>
                        <button
                            className="icon-button"
                            onClick={() => setMaximizedChart('machinesOutOfOrder')}
                            style={{ cursor: 'pointer' }}
                        >
                            <MaximizeIcon color="#666" />
                        </button>
                    </Box>
                </Box>
                {/* Scroll hint for mobile */}
                <Box sx={{
                    display: { xs: 'flex', md: 'none' },
                    justifyContent: 'center',
                    padding: '6px 0',
                    borderTop: '1px solid #e6e6e6',
                    backgroundColor: '#fafafa'
                }}>
                    <Typography sx={{
                        fontSize: '11px',
                        color: '#999',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        ← Scroll to see more →
                    </Typography>
                </Box>
                {/* Scrollable table container for mobile */}
                <Box className="table-container" sx={{
                    overflowX: 'auto',
                    width: '100%',
                    WebkitOverflowScrolling: 'touch'
                }}>
                    <table>
                        <thead>
                            <tr>
                                <th>Machine Name</th>
                                <th>Issue</th>
                                <th>Downtime</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {machinesOutOfOrder.length > 0 ? (
                                machinesOutOfOrder.map((machine, index) => (
                                    <tr key={index}>
                                        <td>{machine.machine_name || machine.name}</td>
                                        <td>{machine.issue || 'N/A'}</td>
                                        <td>{machine.downtime || 'N/A'}</td>
                                        <td>
                                            <span className="status-badge">
                                                {machine.status || 'Out of order'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
                                        No machines out of order
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Box>
            </Box>
        </Box >
    );
};

export default MachineUsageTab;
